<template>
  <Modal
    ref="trainingSessionDetailsModal"
    :title="'Session Details'"
    :width="'100%'"
    :height="'100%'"
    @closed="handleModalClosed"
  >
    <template #modal-main>
      <div class="training-session">
        <div class="session-data">


          <div class="tab-selector">
            <input
              id="option-model-params"
              v-model="tab"
              type="radio"
              name="tab-selector"
              :value="'model-params'"
            >
            <label for="option-model-params">Model Parameters--</label>
            <!-- <input
              id="option-metrics"
              v-model="tab"
              type="radio"
              name="tab-selector"
              :value="'metrics'"
            >
            <label for="option-metrics">Metrics</label> -->
            <input
              id="option-logs"
              v-model="tab"
              type="radio"
              name="tab-selector"
              :value="'logs'"
            >
            <label for="option-logs">System Logs</label>
          </div>
          <div v-if="tab==='model-params'" class="params mt-2">
            <ShikiHighlight
              class="model-params scrollbar shiki-model-params"
              :code="model_parameters"
              lang="yaml"
              theme="vitesse-dark"
            />
          </div>
          <div v-else-if="tab==='metrics'" class="metrics mt-2">
            <div id="training-session-system-logs" class="text-area-details-white scrollbar">
              <div
                v-for="(value, name, index) in metrics"
                :key="index"
                class="list-item"
                :title="value"
              >
                <label>{{ name }}</label>
                <span>{{ value }}</span>
              </div>
            </div>
          </div>
          <div v-else-if="tab==='logs'" class="system-logs mt-2">
            <div id="training-session-system-logs" class="text-area-box scrollbar">
              <RecycleScroller
                v-slot="{ item }"
                class="logs-scroller"
                :items="logLines"
                :item-size="20"
              >
                <div class="log-line">{{ item }}</div>
              </RecycleScroller>
            </div>
          </div>
        </div>
        <div class="artifact-list">
          <h3>Download Raw Model</h3>
          <div class="artifacts scrollbar">
            <DownloadModelCard
              v-for="(artifact, i) in artifacts"
              :key="i"
              :title="artifact.name"
              :modelType="artifact.modelType"
              class="download-model-card"
              @click="handleDownloadModel(artifact)"
            />
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import {
  ref, computed, onBeforeUnmount,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import * as YAML from 'yaml';
import { useTimeoutPoll } from '@vueuse/core';
// import { useToast } from 'vue-toast-notification';
import DownloadModelCard from './DownloadModelCard.vue';
import 'vue-toast-notification/dist/theme-sugar.css';
import ShikiHighlight from "@/components/ShikiHighlight.vue";
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import HelpTip from '../../HelpTip.vue';

const props = defineProps({
  session: {
    type: Object,
    default: () => ({}),
  },
  permissions: {
    type: Object,
    default: () => ({}),
  },
});

// Refs
const trainingSessionDetailsModal = ref(null);
const tab = ref('model-params');
const logs = ref(null);
const artifacts = ref([]);
const logLines = ref([]);
// const $toast = useToast();

const metrics = computed(() => {
  const result = {};
  for (const [key, value] of Object.entries(props.session?.metrics || {})) {
    if (key !== 'timings' && key !== 'model' && key !== 'dataset'
      && key !== 'class_histogram_data' && key !== 'confusion_matrix_data' && key !== 'precision_recall_data') {
      result[key] = value;
    }
  }
  return result;
});

const model_parameters = computed(() => YAML.stringify(props.session?.params?.model_params || {}, { indent: 8 }));

// Lifecycle hooks
onBeforeUnmount(() => {
  pauseGetLogs();
});

// Methods
const showModal = async () => {
  trainingSessionDetailsModal.value.showModal();
  resumeGetLogs();
  artifacts.value = await listTrainingSessionArtifacts()
    .catch((err) => {
      // commenting this since no artifacts is not an error
      // $toast.open({
      //   queue: true,
      //   duration: 5000,
      //   message: `Error occured while fetching artifacts`,
      //   type: 'error',
      //   position: 'top',
      // });
    });
};

const handleModalClosed = () => {
  tab.value = 'model-params';
  logs.value = null;
  pauseGetLogs();
};

const closeModal = () => {
  handleModalClosed();
  trainingSessionDetailsModal.value.closeModal();
};

const getLogs = async () => {
  if (props.session && props.session.id) {
    const dataConnect = new DatastoreConnect();
    const resp = await dataConnect.getTrainingSessionLogs({
      session_id: props.session.id,
    });

    let temp = "";
    if (!resp || resp.error || !resp.result) {
      temp += `Trainer may take some time to create logs file for "${props.session.name}".`;
    } else {
      temp += resp.result;
    }

    logs.value = temp;
  } else {
    logs.value = null;
  }

  if (logs.value) {
    logLines.value = logs.value.split('\n');
  }
};

const { isActive: isGetLogsActive, pause: pauseGetLogs, resume: resumeGetLogs } = useTimeoutPoll(getLogs, 5000);

const handleDownloadModel = (model) => {
  const url = `/download_model?training_session_id=${props.session.id}&file=${model.name}`;

  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', ''); // This tells the browser to download instead of navigate

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const listTrainingSessionArtifacts = async () => {
  const dataConnect = new DatastoreConnect();
  return dataConnect.getTrainingSessionArtifacts({
    training_session_id: props.session.id,
  })
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error.message);
      }
      return resp.result;
    })
    .catch((error) => {
      throw error;
    });
};

// Expose methods to the template
defineExpose({
  showModal,
  closeModal,
});

</script>

<style lang="scss" scoped>

.training-session{
  display: flex;
  flex-direction: row;
  height: 100%;
}

.session-data {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  width: calc(100% - 400px);
  padding: 16px;

  ul {
    list-style-type: none;
  }

  h3 {
    margin: 0;
    margin-bottom: 10px;
    font-size: 1rem;
    text-align: left;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  .text-area-box {
    display: flex;
    flex-direction: row;
    border-radius: 4px;
    background: #121212;
    border: 1px solid grey;
    height: 100%;
    // padding: 15px;
    overflow-y: auto;

    span {
      color: white;
      font-weight: 700;
      font-size: 0.9rem;

      pre {
        font-family: 'Courier New', monospace !important;
        text-align: left;
      }
    }
  }

  .text-area-details-white {
    display: grid;
    grid-template-columns: repeat(auto-fit, 450px);
    grid-auto-rows: 50px;
    grid-gap: 20px 5px;
    height: 100%;
    border-radius: 4px;
    background: var(--color-white-100);
    border: 1px solid var(--color-primary-100);
    padding: 15px;
    overflow-y: auto;

    .list-item {
      height: fit-content;
      display: flex;
      flex-direction: column;
      text-align: left;
      padding: 5px;

      label {
        @include themify() {
          color: tint-color(themed('body-text-color-secondary'), 20%);
        }
        font-weight: 700;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      span {
        @include themify() {
          color: themed('body-text-color');
        }
        font-weight: 600;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .system-logs, .metrics, .params {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }
}

.model-params {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 4px;
  background: var(--color-white-100);
  // border: 1px solid var(--color-primary-100);
  overflow-y: auto;
}

.artifact-list {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 16px;
  max-width: 400px;

  h3 {
    margin: 0;
    font-size: 1rem;
    text-align: left;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }
}

.tab-selector {
  display: flex;
  flex-direction: row;
}

.tab-selector input[type="radio"] {
  display: none;
}

.tab-selector label {
  padding: 2px 5px;
  margin-right: 10px;
  width: fit-content;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
}

.tab-selector input[type="radio"]:checked+label {
  @include themify() {
    color: themed('color-primary-500');
    border-bottom: 2px solid themed('color-primary-500');
  }
}

.artifacts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 0;
  padding: 4px;
}

.download-model-card {
  cursor: pointer;
  &:hover {
    box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.375), 0 0 2px 2px rgba(0, 0, 0, 0.125);
  }
}

.shiki-model-params {
  text-align: left;
  white-space: pre-wrap;
}

:deep(.shiki-model-params pre) {
  padding: 16px;
  flex: 1 1 auto;
  overflow: auto;
}

.logs-scroller {
  height: 100%;
  width: 100%;
}

.log-line {
  text-align: left;
  white-space: pre;
  color: white;
}

:deep(.vue-recycle-scroller__item-wrapper) {
  overflow: visible !important;
}

:deep(.vue-recycle-scroller) {
  overflow: auto !important;
  padding: 15px;
}
</style>
